import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { Button, message, Popconfirm, Space, Spin, Switch, Table } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { tokenVids } from "@extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./messages-tokens.css";
import MessagesToken from "./messages-token";

const vidLabel = value => tokenVids.find(t => t.value === value)?.label ?? "—";

const MessagesTokens = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [providers, setProviders] = useState([]);
  const [spaces, setSpaces] = useState([]);

  const getData = () => {
    setLoading(true);
    serverFetch("token?internalOnly=false")
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка загрузки");
      });
  };

  const getProviders = () => {
    serverFetch("token/Providers")
      .then(data => setProviders(data))
      .catch(e => message.error(e.userMessage || "Ошибка загрузки провайдеров"));
  };

  const getSpaces = () => {
    serverFetch("Cyber/spaces")
      .then(data => setSpaces(Array.isArray(data) ? data : []))
      .catch(() => setSpaces([]));
  };

  useEffect(() => {
    getData();
    getProviders();
    getSpaces();
  }, []);

  const handleAdd = () => {
    setSelected(0);
  };

  const handleDelete = tokenId => {
    setLoading(true);
    serverFetch(`token/${tokenId}`, { method: "DELETE" })
      .then(() => {
        message.success("Токен удален");
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка при удалении");
      });
  };

  const handleToggleActive = (record, checked) => {
    setLoading(true);
    serverFetch("token", {
      method: "PUT",
      bodyData: { ...record, active: checked },
    })
      .then(() => {
        message.success("Сохранено");
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка сохранения");
      });
  };

  const handleSuccess = () => {
    message.success(selected === 0 ? "Канал добавлен" : "Канал обновлен");
    getData();
  };

  const providerLabel = value =>
    value != null && value !== "" ? providers.find(p => p.id === value)?.name || value : "—";

  const columns = [
    {
      title: "",
      width: 70,
      render: (_, record) => (
        <div className="actions-on-hover" style={{ display: "flex", gap: 4 }}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setSelected(record.id)} />
          <Popconfirm
            title="Подтверждение удаления"
            description="Вы уверены, что хотите удалить этот канал?"
            onConfirm={() => handleDelete(record.id)}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
    { dataIndex: "id", title: "#", width: 80 },
    { dataIndex: "idx", title: "Порядок", width: 100 },
    { dataIndex: "name", title: "Наименование", width: 200 },
    { dataIndex: "code", title: "Код", width: 300 },
    { dataIndex: "vid", title: "Вид", width: 120, render: value => vidLabel(value) },
    {
      dataIndex: "spaceId",
      title: "Киберменеджер",
      width: 200,
      render: value => (value != null ? spaces.find(s => s.id === value)?.name ?? value : "-"),
    },
    { dataIndex: "provider", title: "Провайдер", width: 150, render: value => providerLabel(value) },
    { dataIndex: "active", title: "Активен", width: 100, render: value => (value ? "Да" : "Нет") },
  ];

  const tableEl = (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="id"
      size="small"
      scroll={{ x: "max-content" }}
      pagination={false}
    />
  );

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="messages-tokens-mobile-list">
        {items.map(record => {
          const hasName = record.name != null && String(record.name).trim() !== "";
          const codeStr = record.code != null ? String(record.code) : "";
          const hasSpaceBinding = record.spaceId != null;
          const spaceText =
            hasSpaceBinding
              ? String(spaces.find(s => s.id === record.spaceId)?.name ?? record.spaceId).trim()
              : "";
          const showSpace = hasSpaceBinding && spaceText !== "";
          return (
            <div
              key={record.id}
              className="messages-tokens-mobile-card"
              role="button"
              tabIndex={0}
              onClick={() => setSelected(record.id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(record.id);
                }
              }}
            >
              <div className="messages-tokens-mobile-card-corner" onClick={e => e.stopPropagation()}>
                <Popconfirm
                  title="Подтверждение удаления"
                  description="Вы уверены, что хотите удалить этот канал?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Удалить"
                  cancelText="Отмена"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={loading}
                    aria-label="Удалить"
                    className="messages-tokens-mobile-delete-btn"
                  />
                </Popconfirm>
              </div>
              <div className="messages-tokens-mobile-row1">
                <div className="messages-tokens-mobile-active" onClick={e => e.stopPropagation()}>
                  <Switch
                    checked={!!record.active}
                    onChange={checked => handleToggleActive(record, checked)}
                    disabled={loading}
                    size="small"
                  />
                </div>
                {hasName ? <div className="messages-tokens-mobile-name">{String(record.name).trim()}</div> : null}
                <span className="messages-tokens-mobile-vid">{vidLabel(record.vid)}</span>
                <span className="messages-tokens-mobile-provider">{providerLabel(record.provider)}</span>
              </div>
              <div className="messages-tokens-mobile-row2">
                <span>#{record.id}</span>
                <span className="messages-tokens-mobile-sep" aria-hidden>
                  ·
                </span>
                <span>Порядок {record.idx ?? "—"}</span>
                {showSpace ? (
                  <>
                    <span className="messages-tokens-mobile-sep" aria-hidden>
                      ·
                    </span>
                    <span title="Киберменеджер" className="messages-tokens-mobile-space">
                      {spaceText}
                    </span>
                  </>
                ) : null}
                <span className="messages-tokens-mobile-sep" aria-hidden>
                  ·
                </span>
                <span className="messages-tokens-mobile-code">{codeStr || "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  return (
    <>
      {isMobile ? (
        <div className="page orders-page--mobile messages-tokens-page--mobile">
          <div className="messages-tokens-mobile-toolbar">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
              aria-label="Добавить"
              className="company-base-header-icon-btn"
            />
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={getData}
              loading={loading}
              aria-label="Обновить"
              className="company-backups-mobile-refresh-btn"
            />
          </div>
          <div className="messages-tokens-mobile-scroll">{mobileListEl}</div>
        </div>
      ) : (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Button type="primary" onClick={handleAdd} disabled={loading}>
              Добавить
            </Button>
            <Button onClick={getData}>Обновить</Button>
          </Space>
          {tableEl}
        </>
      )}
      {selected !== null && (
        <MessagesToken onClose={() => setSelected(null)} onSuccess={handleSuccess} id={selected} providers={providers} spaces={spaces} />
      )}
    </>
  );
};

export default MessagesTokens;
